// The Acquire Ritual: a 4.4-second ceremony on Shopify product pages.
// Triggered by clicking an element with the data-acquire hook (a real add-to-cart
// button). The cart add fires in parallel with the opening beats, but the ceremony
// only says ACQUIRED after /cart/add.js has actually succeeded. On failure the
// ritual stops, un-affirms, and shows a persistent error until dismissed.
// The .acquire-btn class is COSMETIC ONLY and is also worn by navigation links
// (Originals' register/enquire CTAs), so behaviour is keyed to data-acquire,
// never to the class.

import { render as renderGlyph, load as loadGlyphs } from './prototype-nsibidi.js';

const GLYPH_RING_IDS = [
  'star-grid', 'ring-crosshair', 'square-x', 'ladder',
  'ring-dot', 'zigzag', 'grid-cross', 'nested-ellipse',
  'journey', 'eight-star', 'eye-lens', 'congress'
];

const RADIUS = 190;
const MOBILE_RADIUS = 135;
const ADD_TIMEOUT_MS = 8000;
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let activeOverlay = null;

function buildOverlay() {
  const overlay = document.createElement('div');
  overlay.className = 'acquire-overlay';
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-live', 'assertive');
  overlay.innerHTML = `
    <div class="acquire-stage">
      <div class="acquire-glyph-ring" id="acquireRing"></div>
      <div class="acquire-centre">
        <div class="acquire-artwork" id="acquireArtwork"><img id="acquireArtworkImg" alt=""></div>
        <div class="acquire-word" id="acquireWord"></div>
        <div class="acquire-union" id="acquireUnion"></div>
        <div class="acquire-error" id="acquireError"></div>
      </div>
    </div>
    <button class="acquire-skip" id="acquireSkip" type="button">Skip</button>
  `;
  document.body.appendChild(overlay);
  return overlay;
}

async function populateRing(ringEl, radius) {
  const count = GLYPH_RING_IDS.length;
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    const glyph = document.createElement('div');
    glyph.className = 'acquire-glyph';
    glyph.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
    glyph.style.animationDelay = `${i * 50}ms`;
    glyph.innerHTML = await renderGlyph(GLYPH_RING_IDS[i], 44, '');
    const svg = glyph.querySelector('svg');
    if (svg) svg.style.animationDelay = `${i * 50 + 120}ms`;
    ringEl.appendChild(glyph);
  }
}

function writeWord(wordEl, text) {
  wordEl.innerHTML = '';
  [...text].forEach((ch, i) => {
    const span = document.createElement('span');
    span.textContent = ch === ' ' ? ' ' : ch;
    span.style.transitionDelay = `${i * 40}ms`;
    wordEl.appendChild(span);
  });
}

function nextFrame() {
  return new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
}

async function addToShopifyCart(variantId, quantity) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ADD_TIMEOUT_MS);
  let res;
  try {
    res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
      signal: controller.signal
    });
  } catch (err) {
    if (err && err.name === 'AbortError') {
      throw new Error('The connection timed out. Check your network and try again.');
    }
    throw new Error('The connection failed. Check your network and try again.');
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'The work could not be added to your bag.');
  }
  return res.json();
}

async function showToast(title) {
  const toast = document.getElementById('bagToast');
  const toastTitle = document.getElementById('bagToastTitle');
  const toastGlyph = document.getElementById('bagToastGlyph');
  if (!toast || !toastTitle) return;
  toastTitle.textContent = title;
  if (toastGlyph) toastGlyph.innerHTML = await renderGlyph('union', 24, '');
  toast.hidden = false;
  await nextFrame();
  toast.classList.add('is-visible');
  setTimeout(() => {
    toast.classList.remove('is-visible');
    setTimeout(() => { toast.hidden = true; }, 500);
  }, 3200);
}

async function runRitual({ variantId, title, image }) {
  if (activeOverlay) return;
  await loadGlyphs();

  const overlay = buildOverlay();
  activeOverlay = overlay;

  const ring = overlay.querySelector('#acquireRing');
  const artworkEl = overlay.querySelector('#acquireArtwork');
  const artworkImg = overlay.querySelector('#acquireArtworkImg');
  const wordEl = overlay.querySelector('#acquireWord');
  const unionEl = overlay.querySelector('#acquireUnion');
  const errorEl = overlay.querySelector('#acquireError');
  const skipBtn = overlay.querySelector('#acquireSkip');

  // Touch devices have no Escape key; the skip copy must match the input.
  skipBtn.textContent = window.matchMedia('(hover: none)').matches ? 'Tap to skip' : 'Press esc to skip';

  const radius = window.innerWidth <= 640 ? MOBILE_RADIUS : RADIUS;
  await populateRing(ring, radius);
  if (image) { artworkImg.src = image; artworkImg.alt = title || ''; }
  unionEl.innerHTML = await renderGlyph('union', 36, '');

  window.AAScrollLock?.lock();
  // iOS Safari ignores body overflow:hidden for touch scrolling; block it at
  // the overlay. Taps are unaffected (only touchmove is prevented).
  const blockTouchScroll = e => { e.preventDefault(); };
  overlay.addEventListener('touchmove', blockTouchScroll, { passive: false });
  const previouslyFocused = document.activeElement;

  await nextFrame();
  overlay.classList.add('is-active');
  window.dispatchEvent(new CustomEvent('acquire:start'));

  // Fire the Shopify add in parallel with the opening beats. The ceremony holds
  // its final word until this settles: ACQUIRED is only ever spoken on a 2xx.
  const cartPromise = addToShopifyCart(variantId, 1)
    .then(result => ({ ok: true, result }))
    .catch(error => {
      console.error('[acquire]', error);
      return { ok: false, error };
    });

  const timers = [];
  const pendingWaits = [];
  let settled = false;
  let closed = false;

  // wait() resolves on its timer OR when the theatre is cancelled, so a
  // dismissed ritual never leaves this async scope pending until unload.
  const wait = ms => new Promise(r => { timers.push(setTimeout(r, ms)); pendingWaits.push(r); });
  function cancelTheatre() {
    timers.forEach(clearTimeout);
    pendingWaits.forEach(r => r());
  }

  function closeOverlay() {
    if (closed) return;
    closed = true;
    cancelTheatre();
    window.removeEventListener('keydown', escHandler);
    overlay.style.opacity = 0;
    setTimeout(() => {
      overlay.remove();
      activeOverlay = null;
      window.AAScrollLock?.unlock();
      try { previouslyFocused?.focus(); } catch {}
      window.dispatchEvent(new CustomEvent('acquire:end'));
    }, 500);
  }

  function showFailure(outcome) {
    if (closed || !overlay.isConnected) return;
    cancelTheatre();
    artworkEl.classList.remove('is-affirming');
    unionEl.classList.remove('is-visible');
    writeWord(wordEl, 'NOT ADDED');
    wordEl.classList.add('is-visible', 'is-error');
    if (errorEl) {
      errorEl.textContent = (outcome.error && outcome.error.message) || 'The work could not be added to your bag.';
      errorEl.classList.add('is-visible');
    }
    overlay.classList.add('can-skip', 'is-failed');
    skipBtn.textContent = 'Close';
  }

  function celebrate() {
    if (window.Bag?.refresh) window.Bag.refresh();
    window.dispatchEvent(new CustomEvent('bag:added', { detail: { title, image } }));
    showToast(title);
  }

  async function conclude() {
    if (settled) return;
    settled = true;
    const outcome = await cartPromise;
    if (closed) {
      // User dismissed before the network settled; stay truthful quietly:
      // refresh the bag on success, never celebrate, never error a gone overlay.
      if (outcome.ok && window.Bag?.refresh) window.Bag.refresh();
      return;
    }
    if (!outcome.ok) { showFailure(outcome); return; }
    writeWord(wordEl, 'ACQUIRED');
    wordEl.classList.add('is-visible');
    await wait(400);
    if (!closed) unionEl.classList.add('is-visible');
    await wait(prefersReducedMotion ? 600 : 1000);
    closeOverlay();
    // The add succeeded and ACQUIRED was spoken: celebrate exactly once,
    // even if the customer dismissed during the closing beats.
    celebrate();
  }

  function dismiss() {
    if (overlay.classList.contains('is-failed')) { closeOverlay(); return; }
    if (!settled) {
      // Dismiss before the truth gate: close NOW (never hold a customer
      // hostage to a slow network) and reconcile in the background.
      settled = true;
      closeOverlay();
      cartPromise.then(outcome => {
        if (outcome.ok) celebrate();
      });
      return;
    }
    closeOverlay();
  }

  // Theatre: opening beats run on their own clock; the truth gate sits at the
  // end. Every beat re-checks closed: a dismissed ritual must never mutate the
  // removed overlay or call conclude() (cancelTheatre resolves pending waits).
  (async () => {
    if (prefersReducedMotion) {
      await wait(300); if (closed) return;
      artworkEl.classList.add('is-visible');
      await wait(300); if (closed) return;
      artworkEl.classList.add('is-affirming');
      await wait(200); if (closed) return;
      conclude();
    } else {
      await wait(500); if (closed) return;
      artworkEl.classList.add('is-visible');
      await wait(2300); if (closed) return;
      artworkEl.classList.add('is-affirming');
      await wait(400); if (closed) return;
      overlay.classList.add('can-skip');
      conclude();
    }
  })();

  skipBtn?.addEventListener('click', dismiss);
  // In the failure state the whole backdrop dismisses (44px buttons are good,
  // the entire screen is better when someone just wants out).
  overlay.addEventListener('click', e => {
    if (overlay.classList.contains('is-failed') && e.target === overlay) closeOverlay();
  });
  const escHandler = e => {
    if (e.key === 'Escape') dismiss();
  };
  window.addEventListener('keydown', escHandler);
}

// Wire up add-to-cart buttons. Behaviour is keyed to the data-acquire hook, NOT
// the .acquire-btn class. .acquire-btn is cosmetic and is also worn by the
// Originals "Register Collector Interest" / "Join the Register" LINKS, which must
// navigate, not run the cart ritual. The variant-id guard is defence in depth.
// (Dead-link regression, PR #26 / #27. Never rebind this handler to a styling class.)
document.addEventListener('click', e => {
  const btn = e.target.closest('[data-acquire]');
  if (!btn) return;
  if (btn.disabled || btn.hasAttribute('disabled')) return;
  if (!btn.dataset.variantId) return;
  e.preventDefault();
  runRitual({
    variantId: btn.dataset.variantId,
    title: btn.dataset.title,
    image: btn.dataset.image
  });
});

window.Acquire = { run: runRitual };
